import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router';

/** A click the app may take over. Anything modified (new tab/window, download)
 *  or already handled belongs to the browser. */
export function isPlainLeftClick(e: MouseEvent<HTMLAnchorElement>): boolean {
  return !e.defaultPrevented && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

interface FdLinkProps {
  to: string;
  className?: string | undefined;
  style?: CSSProperties | undefined;
  /** Runs first on every click. It must not cancel the click; routing still follows. */
  onClick?: (() => void) | undefined;
  children: ReactNode;
}

/**
 * A real `<a href>` for in-shell routes on the front-door pages: a plain left
 * click routes client-side, and everything else (Cmd/Ctrl-click, middle click,
 * copy link) behaves like any link. Used instead of Cloudscape `Link`/`Button`
 * because these are styled calls to action and cards on a custom canvas.
 */
export function FdLink({ to, className, style, onClick, children }: FdLinkProps) {
  const navigate = useNavigate();
  return (
    <a
      href={to}
      className={className}
      style={style}
      onClick={(e) => {
        onClick?.();
        if (!isPlainLeftClick(e)) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}
